import { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, AlertTriangle, CalendarX, CheckCircle2, Circle, CalendarPlus } from 'lucide-react';

const API = '/api';

/**
 * The planning strip: the next six weeks across every surface, one lane each, one column
 * per week. A cell with nothing real in it is drawn as a GAP - amber, dashed, labelled - so
 * a lane that has gone quiet is visible from across the room. Randall's ask: "making sure we
 * have things planned out."
 *
 * READ-ONLY. It renders what /api/planning/strip returns, which is shaped from the calendar's
 * own events. Clicking an item deep-links to where that draft is reviewed; nothing here
 * approves, schedules or sends, and the strip cannot create a post.
 */

const LANE_COLOR = {
  social: '#48B64C',
  email: '#5B8DEF',
  newsletter: '#C7A67A',
  weekly_fitter: '#E0A82E',
};

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function chipStyle(item, color) {
  // A slot left open ON PURPOSE (18 Sep 2026): calm grey, dotted - a decision, not a gap.
  if (item.open) return { color: '#8A94A6', border: '1px dotted #8A94A6', backgroundColor: 'transparent' };
  if (item.ghost) return { color: '#8A94A6', border: '1px dashed #8A94A6', backgroundColor: 'transparent' };
  if (item.planned) return { color, border: `1px dotted ${color}`, backgroundColor: `${color}08` };
  return { color, borderLeft: `2px solid ${color}`, backgroundColor: `${color}20` };
}

export default function PlanningStrip({ onNavigate }) {
  const [start, setStart] = useState(() => isoDate(new Date()));
  const [strip, setStrip] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API}/planning/strip`, { params: { start, weeks: 6 } })
      .then((res) => { if (live) { setStrip(res.data); setError(''); } })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || 'Could not load the strip'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [start]);

  const shift = (weeks) => {
    const d = new Date(`${start}T12:00:00`);
    d.setDate(d.getDate() + 7 * weeks);
    setStart(isoDate(d));
  };

  const open = (item) => {
    if (!item.module || !onNavigate) return;
    // Same deep-link shape as the dashboard row: the target reads ?tab= at mount.
    try {
      const p = new URLSearchParams();
      p.set('module', item.module);
      if (item.tab) p.set('tab', item.tab);
      window.history.replaceState(null, '', `?${p.toString()}`);
    } catch { /* URL update is best-effort */ }
    onNavigate(item.module, item.tab ? { tab: item.tab } : undefined);
  };

  const today = isoDate(new Date());

  return (
    <div className="space-y-5" data-testid="planning-strip">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">PLANNING</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            The next six weeks across every surface. A gap is a week with nothing planned. Nothing here posts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="strip-prev" onClick={() => shift(-1)} className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green hover:border-hg-green"><ChevronLeft size={16} /></button>
          <button data-testid="strip-today" onClick={() => setStart(today)} className="px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-text">This week</button>
          <button data-testid="strip-next" onClick={() => shift(1)} className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green hover:border-hg-green"><ChevronRight size={16} /></button>
        </div>
      </div>

      {strip && (
        <div className="flex items-center gap-4 text-xs font-sans text-hg-text-dim flex-wrap" data-testid="strip-summary">
          <span className={`inline-flex items-center gap-1.5 ${strip.gap_count ? 'text-amber-300' : 'text-hg-green'}`}>
            {strip.gap_count ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
            {strip.gap_count} gap{strip.gap_count === 1 ? '' : 's'} in {strip.cell_count} lane-weeks
          </span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-dotted border-hg-text-dim" /> planned draft</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-hg-text-dim/30" /> approved / scheduled / sent</span>
          <span className="inline-flex items-center gap-1.5"><CalendarX size={12} /> gap</span>
          <span className="inline-flex items-center gap-1.5"><CalendarPlus size={12} /> open slot (left open on purpose)</span>
        </div>
      )}

      {loading && <div className="text-center py-12 text-hg-text-dim text-sm">Loading…</div>}
      {error && <div className="text-sm text-red-300 font-sans" data-testid="strip-error">{error}</div>}

      {!loading && strip && (
        <div className="bg-hg-surface border border-hg-border rounded-sm overflow-x-auto">
          <div className="min-w-[960px]">
            <div className="grid border-b border-hg-border" style={{ gridTemplateColumns: `140px repeat(${strip.weeks.length}, minmax(0, 1fr))` }}>
              <div className="px-3 py-2 text-[11px] font-sans uppercase tracking-wider text-hg-text-dim">Surface</div>
              {strip.weeks.map((w) => {
                const isThisWeek = w.start <= today && today <= w.end;
                return (
                  <div key={w.index} className={`px-2 py-2 text-[11px] font-sans uppercase tracking-wider border-l border-hg-border ${isThisWeek ? 'text-hg-green' : 'text-hg-text-dim'}`}>
                    w/c {w.label}{isThisWeek ? ' · this week' : ''}
                  </div>
                );
              })}
            </div>
            {strip.lanes.map((lane) => (
              <div key={lane.key} className="grid border-b border-hg-border last:border-b-0" data-testid={`strip-lane-${lane.key}`}
                   style={{ gridTemplateColumns: `140px repeat(${strip.weeks.length}, minmax(0, 1fr))` }}>
                <div className="px-3 py-3 flex items-start gap-2">
                  <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: LANE_COLOR[lane.key] || '#8A94A6' }} />
                  <div>
                    <div className="text-sm font-sans text-hg-text">{lane.label}</div>
                    {lane.gaps > 0 && <div className="text-[10px] font-sans text-amber-300">{lane.gaps} gap{lane.gaps === 1 ? '' : 's'}</div>}
                  </div>
                </div>
                {lane.cells.map((cell) => (
                  <div key={cell.week} data-testid={`strip-cell-${lane.key}-${cell.week}`}
                       className={`border-l border-hg-border p-1.5 min-h-[72px] space-y-1 ${cell.gap ? 'bg-amber-400/5' : ''}`}>
                    {cell.gap && (
                      <div data-testid={`strip-gap-${lane.key}-${cell.week}`}
                           className="h-full min-h-[60px] flex items-center justify-center gap-1 text-[10px] font-sans uppercase tracking-wider text-amber-300 border border-dashed border-amber-400/60 rounded-sm">
                        <CalendarX size={11} /> gap
                      </div>
                    )}
                    {cell.items.filter((i) => !i.ghost || cell.gap).map((item) => (
                      <button key={item.id} onClick={() => open(item)} title={item.preview || item.title}
                              className="w-full text-left px-1.5 py-1 rounded-sm text-[10px] font-sans truncate hover:opacity-80 flex items-center gap-1"
                              style={chipStyle(item, LANE_COLOR[lane.key] || '#8A94A6')}>
                        {item.open && <CalendarPlus size={10} className="shrink-0" />}
                        {!item.ghost && !item.open && (item.ready
                          ? <CheckCircle2 size={10} className="shrink-0" />
                          : <Circle size={10} className="shrink-0" />)}
                        <span className="truncate">{item.date.slice(5).replace('-', '/')} · {item.title}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
